import React, { useState, useEffect, useRef } from 'react';

const AccountSearchInput = ({
  value,
  onChange,
  placeholder = 'Search company name...',
  required = false,
  allowNew = false,
  onNewCompany,
  initialName = '',
  onContactsLoaded,
}) => {
  const [query, setQuery] = useState(initialName || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState(initialName || '');
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const token = localStorage.getItem('access_token') || '';

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowContacts(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    setSelectedName('');
    setContacts([]);
    setShowContacts(false);
    onChange(null, '');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setOpen(false); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/v1/accounts/search?q=${encodeURIComponent(q)}&limit=8`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        const items = Array.isArray(data) ? data : (data.accounts || data.results || data.items || []);
        setResults(items);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
  };

  const fetchContacts = async (accountId) => {
    try {
      const res = await fetch(`/api/v1/contacts/search/by-account/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      setContacts(items);
      if (onContactsLoaded) onContactsLoaded(items);
      if (items.length === 1) {
        // Auto-fill if only one contact
        if (onContactsLoaded) onContactsLoaded(items);
      }
      if (items.length > 0) setShowContacts(true);
    } catch { setContacts([]); }
  };

  const selectAccount = async (account) => {
    const name = account.name || account.Name;
    setQuery(name);
    setSelectedName(name);
    setOpen(false);
    setResults([]);
    onChange(account.id, name);
    await fetchContacts(account.id);
  };

  const selectContact = (contact) => {
    setShowContacts(false);
    if (onContactsLoaded) onContactsLoaded([contact], true); // true = auto-selected
  };

  const handleNewCompany = () => {
    setOpen(false);
    setShowContacts(false);
    if (onNewCompany) onNewCompany(query);
  };

  const isSelected = !!value && !!selectedName;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className={`w-full px-3 py-2 pr-8 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            isSelected ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-white'
          }`}
        />
        <div className="absolute right-2 top-2.5 text-sm">
          {loading && <span className="text-gray-400">⏳</span>}
          {!loading && isSelected && <span className="text-green-500">✓</span>}
          {!loading && !isSelected && query.length >= 2 && <span className="text-gray-400">🔍</span>}
        </div>
      </div>

      {query.length >= 2 && !isSelected && !loading && !open && (
        <p className="mt-1 text-xs text-amber-600">⚠️ Please select from the list</p>
      )}

      {/* Company dropdown */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="px-3 py-2 text-sm text-gray-500">No companies found</div>
          )}
          {results.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => selectAccount(account)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{account.name || account.Name}</span>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  {account.enq_count > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{account.enq_count} enq</span>
                  )}
                  {account.sale_type && (
                    <span className="bg-gray-100 px-1.5 py-0.5 rounded">{account.sale_type}</span>
                  )}
                </div>
              </div>
              {account.location && <div className="text-xs text-gray-400">{account.location}</div>}
            </button>
          ))}
          {allowNew && query.length >= 2 && (
            <button
              type="button"
              onClick={handleNewCompany}
              className="w-full text-left px-3 py-2 hover:bg-green-50 border-t border-gray-200 text-sm text-green-600 font-medium"
            >
              + Add "{query}" as new company
            </button>
          )}
        </div>
      )}

      {/* Contact picker dropdown */}
      {showContacts && contacts.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-200 text-xs font-medium text-blue-700">
            Select contact to auto-fill details:
          </div>
          {contacts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => selectContact(c)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <div className="text-sm font-medium text-gray-900">{c.Name || c.name}</div>
              <div className="text-xs text-gray-400">
                {c.Email1 || c.email_1 || '—'} · {c.Phone1 || c.phone_1 || '—'}
              </div>
              {c.Designation || c.designation ? (
                <div className="text-xs text-gray-400">{c.Designation || c.designation}</div>
              ) : null}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowContacts(false)}
            className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 border-t"
          >
            Skip — enter manually
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountSearchInput;
